import React, { useEffect, useRef, useState } from "react"
import { Tooltip } from "antd"
import { WarningOutlined } from "@ant-design/icons"
import { useDrop } from "react-dnd"
import Strings from "utils/strings"
import { Route, RouteAddress } from "../../types"
import { PendingMove, TimeDomain } from "./types"
import {
	DND_TYPE,
	ROW_HEIGHT,
	SIDEBAR_WIDTH,
	changeTypeFor,
	fromInsetPercent,
	isOutOfFormatOrder,
	pickupStops,
	toInsetPercent,
} from "./utils"
import DepotMarker from "./DepotMarker"
import PickupMarker from "./PickupMarker"
import Pill from "./Pill"

interface DragItem {
	orderId: string
	fromRouteId: string
}

const RouteRow: React.FC<{
	route: Route
	color: string
	stops: RouteAddress[]
	timeDomain: TimeDomain | null
	trackWidth: number
	positionOverrides: Record<string, number>
	pendingMoves: Map<string, PendingMove>
	sequenceByOrderId: Record<string, number>
	onDropOrder: (
		orderId: string,
		fromRouteId: string,
		toRouteId: string,
		dropTime?: number
	) => void
	selectedOrderId?: string | null
	onSelectOrder?: (orderId: string) => void
	compact?: boolean
}> = ({
	route,
	color,
	stops,
	timeDomain,
	trackWidth,
	positionOverrides,
	pendingMoves,
	sequenceByOrderId,
	onDropOrder,
	selectedOrderId,
	onSelectOrder,
	compact,
}) => {
	const trackElRef = useRef<HTMLDivElement | null>(null)
	// Where the dragged pill would land if released right now — drawn as a
	// preview line so the drop position is obvious instead of just tinting
	// the whole row.
	const [hoverPercent, setHoverPercent] = useState<number | null>(null)

	const capacity = route.vehicleInfo?.capacity
	const overCapacity = !!capacity && stops.length > capacity

	// Real ETA when we have one, else the given fallback (0-100, inset applied here).
	const positionForTime = (eta: number | undefined, fallback: number) => {
		if (timeDomain && eta) {
			const clamped = Math.min(
				Math.max(eta, timeDomain.start),
				timeDomain.start + timeDomain.range
			)
			return toInsetPercent(
				((clamped - timeDomain.start) / timeDomain.range) * 100,
				trackWidth
			)
		}
		return toInsetPercent(fallback, trackWidth)
	}

	const stopEtas = stops.map(
		(addr) => positionOverrides[addr.order.orderId] ?? addr.eta
	)

	// Depots before the first delivery are the "start" leg, after it the "end" leg.
	const allAddresses = route.addresses || []
	const firstDeliveryIndex = allAddresses.findIndex(
		(a) => !a.isDepot && a.order?.orderId
	)
	const depotMarkers = allAddresses
		.map((addr, index) => ({ addr, index }))
		.filter(({ addr }) => addr.isDepot)
		.map(({ addr, index }) => {
			const isStart = firstDeliveryIndex === -1 || index < firstDeliveryIndex
			return {
				key: `depot-${index}`,
				addr,
				isStart,
				position: positionForTime(addr.eta, isStart ? 0 : 100),
			}
		})

	const pickups = pickupStops(route)
	const pickupMarkers = pickups.map((addr, index) => ({
		key: `pickup-${index}`,
		addr,
		position: positionForTime(
			addr.eta,
			pickups.length > 1 ? (index / (pickups.length - 1)) * 100 : 50
		),
	}))

	// Positioned by real ETA only, no forced minimum gap — close stops overlap.
	const stopPositions = stopEtas.map((eta, i) =>
		positionForTime(
			eta,
			stopEtas.length > 1 ? (i / (stopEtas.length - 1)) * 100 : 50
		)
	)

	// Depot-to-depot, not just first-to-last-stop — the band reaches the
	// warehouse icon and bounds where a stop can be dropped.
	const allBandPositions = [
		...stopPositions,
		...depotMarkers.map((d) => d.position),
	]
	const bandStart = allBandPositions.length ? Math.min(...allBandPositions) : 0
	const bandEnd = allBandPositions.length ? Math.max(...allBandPositions) : 100

	// Keeps a dragged delivery within its own PICKUP group's boundary ("Ajustar
	// ETAs" sends the exact sequence, no solver to catch this). Only applies to
	// same-route reorders — a stop isn't in `stops` yet mid cross-route drag.
	const pickupTimes = pickups
		.map((addr) => addr.eta)
		.filter((t): t is number => t != null)
		.sort((a, b) => a - b)
	const stopEtaByOrderId = new Map(
		stops.map((addr, i) => [addr.order.orderId, stopEtas[i]])
	)
	const pickupWindowFor = (
		orderId: string
	): { start?: number; end?: number } | null => {
		if (pickupTimes.length === 0) return null
		const currentEta = stopEtaByOrderId.get(orderId)
		if (currentEta == null) return null
		const before = pickupTimes.filter((t) => t <= currentEta)
		const after = pickupTimes.filter((t) => t > currentEta)
		return {
			start: before.length ? before[before.length - 1] : undefined,
			end: after.length ? after[0] : undefined,
		}
	}

	// Pointer position as a track percent, clamped to the route's span/PICKUP window.
	const percentFromMonitor = (
		monitor: { getClientOffset: () => { x: number; y: number } | null },
		excludeOrderId?: string
	): number | null => {
		const offset = monitor.getClientOffset()
		if (!offset || !trackElRef.current) return null
		const rect = trackElRef.current.getBoundingClientRect()
		const rawPercent = ((offset.x - rect.left) / rect.width) * 100

		let minBound = bandStart
		let maxBound = bandEnd
		const window = excludeOrderId ? pickupWindowFor(excludeOrderId) : null
		if (window) {
			if (window.start != null) {
				minBound = Math.max(minBound, positionForTime(window.start, 0))
			}
			if (window.end != null) {
				maxBound = Math.min(maxBound, positionForTime(window.end, 100))
			}
		}

		return Math.min(Math.max(rawPercent, minBound), maxBound)
	}

	const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
		accept: DND_TYPE,
		hover: (item, monitor) =>
			setHoverPercent(percentFromMonitor(monitor, item.orderId)),
		drop: (item, monitor) => {
			let dropTime: number | undefined
			const percent = percentFromMonitor(monitor, item.orderId)
			if (percent != null && timeDomain) {
				const timePercent = fromInsetPercent(percent, trackWidth)
				const clampedPercent = Math.min(Math.max(timePercent, 0), 100)
				dropTime = timeDomain.start + (clampedPercent / 100) * timeDomain.range
			}
			setHoverPercent(null)
			onDropOrder(item.orderId, item.fromRouteId, route._id, dropTime)
		},
		collect: (monitor) => ({ isOver: monitor.isOver() }),
	})

	// Clears the preview line once the drag moves off this row entirely.
	useEffect(() => {
		if (!isOver) setHoverPercent(null)
	}, [isOver])

	return (
		<div style={{ display: "flex", borderTop: "1px solid #f0f0f0" }}>
			<div
				style={{
					width: SIDEBAR_WIDTH,
					flexShrink: 0,
					position: "sticky",
					left: 0,
					background: "#fff",
					zIndex: 3,
					padding: "8px 12px",
					borderLeft: `4px solid ${color}`,
					display: "flex",
					flexDirection: "column",
					justifyContent: "center",
				}}
			>
				<span
					style={{
						fontWeight: 500,
						fontSize: 13,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
					title={route.name}
				>
					{route.name}
				</span>
				<span
					style={{
						fontSize: 12,
						color: "#8c8c8c",
						display: "flex",
						alignItems: "center",
						gap: 4,
					}}
				>
					{route.driverInfo?.name || "-"}
					{overCapacity && (
						<Tooltip
							title={Strings.formatString(
								Strings.routes.moveOrdersCapacityWarning,
								stops.length,
								capacity
							)}
						>
							<WarningOutlined style={{ color: "#faad14" }} />
						</Tooltip>
					)}
				</span>
			</div>

			<div
				ref={(node) => {
					trackElRef.current = node
					drop(node)
				}}
				style={{
					width: trackWidth,
					flexShrink: 0,
					position: "relative",
					height: ROW_HEIGHT,
				}}
			>
				{isOver && hoverPercent != null && (
					<div
						style={{
							position: "absolute",
							left: `${hoverPercent}%`,
							top: 4,
							bottom: 4,
							width: 2,
							background: color,
							transform: "translateX(-50%)",
							zIndex: 2,
							pointerEvents: "none",
						}}
					/>
				)}

				{stopPositions.length > 0 && (
					<div
						style={{
							position: "absolute",
							left: `${bandStart}%`,
							width: `${Math.max(bandEnd - bandStart, 0.5)}%`,
							top: "50%",
							height: 5,
							transform: "translateY(-50%)",
							background: color,
							opacity: 0.22,
							borderRadius: 3,
						}}
					/>
				)}

				{depotMarkers.map((depot) => (
					<DepotMarker
						key={depot.key}
						addr={depot.addr}
						left={depot.position}
						color={color}
					/>
				))}

				{pickupMarkers.map((pickup) => (
					<PickupMarker
						key={pickup.key}
						addr={pickup.addr}
						left={pickup.position}
					/>
				))}

				{stops.map((addr, index) => (
					<Pill
						key={addr.order.orderId}
						addr={addr}
						left={stopPositions[index]}
						eta={stopEtas[index]}
						isOverridden={addr.order.orderId in positionOverrides}
						color={color}
						routeId={route._id}
						changeType={changeTypeFor(
							addr.order.orderId,
							pendingMoves,
							positionOverrides
						)}
						sequence={sequenceByOrderId[addr.order.orderId]}
						outOfFormatWarning={
							route.routeType === "bike" && isOutOfFormatOrder(addr.order)
						}
						selected={addr.order.orderId === selectedOrderId}
						onSelect={() => onSelectOrder?.(addr.order.orderId)}
						compact={compact}
					/>
				))}
			</div>
		</div>
	)
}

export default RouteRow
